import { ChangeDetectionStrategy, Component, signal, inject } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { Router, RouterOutlet, NavigationEnd } from "@angular/router";
import { NavbarComponent } from './navbar/navbar';
import {  MsalService } from '@azure/msal-angular';
import { MsalBroadcastService } from '@azure/msal-angular';
import type { EventMessage} from '@azure/msal-browser';
import { EventType } from '@azure/msal-browser';
import { AppInsightsService } from './shared/AppInsights/app-insights.service';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  imports: [ RouterOutlet, NavbarComponent, HttpClientModule],
  templateUrl: './app.html',
  styleUrl: './app.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class App {
  protected readonly title = signal('app');
  private readonly msalEvents = inject(MsalBroadcastService);
  private readonly authService = inject(MsalService);
  private readonly appInsights = inject(AppInsightsService);
  private readonly router = inject(Router);
  protected readonly authLoading = signal(false);
  
  private appLoadTracked = false;
  private userSessionTracked = false;

  constructor() {
    // MSAL authentication event handling
    this.msalEvents.msalSubject$.subscribe((ev: EventMessage) => {
      if (ev.eventType === EventType.HANDLE_REDIRECT_START || ev.eventType === EventType.LOGIN_START) {
        this.authLoading.set(true);
      }
      if (ev.eventType === EventType.HANDLE_REDIRECT_END || ev.eventType === EventType.LOGIN_SUCCESS || ev.eventType === EventType.ACQUIRE_TOKEN_SUCCESS || ev.eventType === EventType.LOGIN_FAILURE || ev.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
        this.authLoading.set(false);
      }
      
      // Track successful login in AppInsights with comprehensive data
      if (ev.eventType === EventType.LOGIN_SUCCESS && ev.payload) {
        try {
          const payload = ev.payload as { 
            account?: { 
              username?: string; 
              name?: string;
              localAccountId?: string;
              homeAccountId?: string;
            };
            idTokenClaims?: Record<string, unknown>;
          };
          
          const account = payload.account;
          if (account?.username) {
            this.appInsights.setAuthenticatedUserContext(account.username, account.homeAccountId);
            
            const sessionData = this.getSessionData();
            this.appInsights.trackEvent('UserLogin', {
              username: account.username,
              displayName: account.name,
              localAccountId: account.localAccountId,
              homeAccountId: account.homeAccountId,
              ...sessionData,
              eventType: 'LOGIN_SUCCESS'
            });
            
            // Track first app load for authenticated user (only once per session)
            if (!this.userSessionTracked) {
              this.trackUserSessionStart(account.username, sessionData);
              this.userSessionTracked = true;
            }
          }
        } catch (error) {
          console.error('Error tracking login event:', error);
        }
      }
      
      // Track authentication failures with detailed error information
      if (ev.eventType === EventType.LOGIN_FAILURE && ev.payload) {
        try {
          const errorPayload = ev.payload as {
            error?: {
              errorCode?: string;
              errorMessage?: string;
              subError?: string;
              name?: string;
              stack?: string;
            };
            errorDesc?: string;
          };
          
          const sessionData = this.getSessionData();
          this.appInsights.trackEvent('AuthenticationFailure', {
            eventType: 'LOGIN_FAILURE',
            errorCode: errorPayload.error?.errorCode,
            errorMessage: errorPayload.error?.errorMessage,
            subError: errorPayload.error?.subError,
            errorName: errorPayload.error?.name,
            errorDescription: errorPayload.errorDesc,
            ...sessionData
          });
          
          // Also track as exception for better visibility
          if (errorPayload.error) {
            const error = new Error(`Authentication failed: ${errorPayload.error.errorMessage || errorPayload.error.errorCode}`);
            error.name = 'AuthenticationError';
            this.appInsights.trackException(error, 2); // Severity: Warning
          }
        } catch (error) {
          console.error('Error tracking login failure:', error);
        }
      }
      
      // Track token acquisition failures
      if (ev.eventType === EventType.ACQUIRE_TOKEN_FAILURE && ev.payload) {
        try {
          const errorPayload = ev.payload as {
            error?: {
              errorCode?: string;
              errorMessage?: string;
              subError?: string;
            };
          };
          
          const sessionData = this.getSessionData();
          this.appInsights.trackEvent('TokenAcquisitionFailure', {
            eventType: 'ACQUIRE_TOKEN_FAILURE',
            errorCode: errorPayload.error?.errorCode,
            errorMessage: errorPayload.error?.errorMessage,
            subError: errorPayload.error?.subError,
            ...sessionData
          });
        } catch (error) {
          console.error('Error tracking token acquisition failure:', error);
        }
      }
      
      // Clear user context on logout
      if (ev.eventType === EventType.LOGOUT_SUCCESS) {
        try {
          const sessionData = this.getSessionData();
          this.appInsights.trackEvent('UserLogout', sessionData);
          this.appInsights.clearAuthenticatedUserContext();
          this.userSessionTracked = false;
        } catch (error) {
          console.error('Error tracking logout event:', error);
        }
      }
    });
    
    // Track route changes in AppInsights with enhanced data
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: unknown) => {
        const navEvent = event as NavigationEnd;
        try {
          const sessionData = this.getSessionData();
          
          // Track initial app load (only once)
          if (!this.appLoadTracked) {
            this.trackAppLoad(navEvent.urlAfterRedirects, sessionData);
            this.appLoadTracked = true;
          }
          
          // Track dashboard view specifically
          if (navEvent.urlAfterRedirects.includes('/dashboard')) {
            this.trackDashboardView(navEvent.urlAfterRedirects, sessionData);
          }
          
          // Track all page views with session data
          this.appInsights.trackPageView(navEvent.urlAfterRedirects, navEvent.urlAfterRedirects);
          this.appInsights.trackEvent('RouteNavigation', {
            url: navEvent.urlAfterRedirects,
            urlBeforeRedirects: navEvent.url,
            ...sessionData
          });
        } catch (error) {
          console.error('Error tracking page view:', error);
        }
      });
    
    // Handle chunk loading errors (old clients after deployment)
    this.setupChunkErrorHandler();
  }

  /**
   * Detects when lazy-loaded chunks fail to load (after deployment) and reloads the page
   */
  private setupChunkErrorHandler(): void {
    // Listen for unhandled promise rejections (chunk loading errors)
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const error = event.reason;
      
      // Check if this is a chunk loading error
      if (this.isChunkLoadError(error)) {
        event.preventDefault(); // Prevent default error handling
        
        console.warn('Chunk loading error detected. New deployment detected - reloading page...', error);
        
        // Track in AppInsights before reload
        try {
          this.appInsights.trackEvent('ChunkLoadError_PageReload', {
            errorMessage: error?.message || 'Unknown chunk load error',
            currentUrl: window.location.href,
            userAgent: window.navigator.userAgent
          });
        } catch (trackError) {
          console.error('Error tracking chunk load error:', trackError);
        }
        
        // Reload the page to get the new chunks
        window.location.reload();
      }
    });
  }

  /**
   * Determines if an error is a chunk loading error
   */
  private isChunkLoadError(error: unknown): boolean {
    if (!error) return false;
    
    const errorString = error.toString().toLowerCase();
    const errorMessage = (error as Error)?.message?.toLowerCase() || '';
    
    // Check for various chunk loading error patterns
    return (
      errorString.includes('loading chunk') ||
      errorString.includes('failed to fetch dynamically imported module') ||
      errorMessage.includes('loading chunk') ||
      errorMessage.includes('failed to fetch dynamically imported module') ||
      errorMessage.includes('dynamically imported module') ||
      (error instanceof TypeError && errorMessage.includes('failed to fetch'))
    );
  }

  /**
   * Get comprehensive session data including browser, OS, location
   */
  private getSessionData(): Record<string, string> {
    const nav = window.navigator;
    
    return {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: nav.userAgent,
      browser: this.getBrowserInfo(),
      platform: nav.platform,
      language: nav.language,
      languages: nav.languages?.join(',') || nav.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset().toString(),
      // Location/region will be determined by AppInsights from IP
      cookieEnabled: nav.cookieEnabled.toString(),
      onLine: nav.onLine.toString(),
      deviceMemory: (nav as { deviceMemory?: number }).deviceMemory?.toString() || 'unknown',
      hardwareConcurrency: nav.hardwareConcurrency?.toString() || 'unknown',
      connectionType: (nav as { connection?: { effectiveType?: string } }).connection?.effectiveType || 'unknown'
    };
  }

  /**
   * Extract browser name from user agent
   */
  private getBrowserInfo(): string {
    const ua = window.navigator.userAgent;
    if (ua.includes('Edg/')) return 'Edge';
    if (ua.includes('Chrome/')) return 'Chrome';
    if (ua.includes('Firefox/')) return 'Firefox';
    if (ua.includes('Safari/') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Opera/') || ua.includes('OPR/')) return 'Opera';
    return 'Unknown';
  }

  /**
   * Track app load event (first time app is loaded)
   */
  private trackAppLoad(initialUrl: string, sessionData: Record<string, string>): void {
    try {
      const accounts = this.authService.instance.getAllAccounts();
      const currentAccount = accounts.length > 0 ? accounts[0] : null;
      
      this.appInsights.trackEvent('AppLoad', {
        initialUrl,
        isAuthenticated: (!!currentAccount).toString(),
        username: currentAccount?.username || 'anonymous',
        accountCount: accounts.length.toString(),
        ...sessionData
      });
    } catch (error) {
      console.error('Error tracking app load:', error);
    }
  }

  /**
   * Track authenticated user session start
   */
  private trackUserSessionStart(username: string, sessionData: Record<string, string>): void {
    try {
      this.appInsights.trackEvent('UserSessionStart', {
        username,
        sessionStart: new Date().toISOString(),
        ...sessionData
      });
    } catch (error) {
      console.error('Error tracking user session start:', error);
    }
  }

  /**
   * Track dashboard view with enhanced data
   */
  private trackDashboardView(url: string, sessionData: Record<string, string>): void {
    try {
      const accounts = this.authService.instance.getAllAccounts();
      const currentAccount = accounts.length > 0 ? accounts[0] : null;
      
      // Extract portfolio ID from URL if present (e.g., /dashboard/123/properties)
      const urlParts = url.split('/');
      const portfolioId = urlParts.length > 2 && urlParts[1] === 'dashboard' ? urlParts[2] : null;
      
      this.appInsights.trackEvent('DashboardView', {
        url,
        username: currentAccount?.username || 'anonymous',
        portfolioId: portfolioId || 'none',
        hasPortfolioSelected: (!!portfolioId).toString(),
        ...sessionData
      });
    } catch (error) {
      console.error('Error tracking dashboard view:', error);
    }
  }

  ngOnIt() {
    this.authService.handleRedirectObservable().subscribe();
  }
}
